import { useCallback, useState } from 'react';
import { getRangeByKey, type TimeFilter } from '@/components/TimeRangeSelector';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';
import {
  dashboardDefaultTimeRange,
  useDashboardDefaultRangeKey,
} from './dashboardDefaultRange';

/** `filter` moved to `zone`, a relative preset re-computed there. */
function inZone(filter: TimeFilter, zone: string): TimeFilter {
  const { range } = filter;
  const preset = range.mode === 'relative' ? getRangeByKey(range.preset) : null;
  return {
    range: preset ? { ...range, ...preset.getRange(zone) } : range,
    timezone: zone,
  };
}

/**
 * A dashboard widget's time filter: the last 7 days in the Settings time zone
 * (the last day for a very busy organization, KIR-5693).
 * When that zone changes (here or in another tab), a filter still in the old
 * zone follows it; one the user moved to another zone in the selector keeps
 * that zone.
 */
export function useDashboardTimeFilter(): [
  TimeFilter,
  (filter: TimeFilter) => void,
] {
  const zone = useDisplayTimeZone();
  const defaultKey = useDashboardDefaultRangeKey();
  const [state, setState] = useState<{ zone: string; filter: TimeFilter }>(
    () => ({
      zone,
      filter: {
        range: dashboardDefaultTimeRange(defaultKey, zone),
        timezone: zone,
      },
    })
  );
  let { filter } = state;
  if (state.zone !== zone) {
    if (filter.timezone === state.zone) filter = inZone(filter, zone);
    // Adjusting state while rendering: React re-renders before committing, so
    // no frame (or fetch) runs on the old zone.
    setState({ zone, filter });
  }
  const setFilter = useCallback(
    (next: TimeFilter) => setState(current => ({ ...current, filter: next })),
    []
  );
  return [filter, setFilter];
}
